import React, { useState } from 'react';
import moment from 'moment';
import Joi from 'joi-browser';
import _ from 'lodash';

import { DATE_TIME_FORMAT } from '../utilities/constants';
import { messageUtility } from '../utilities/messageUtility';

import { CCUS_STATUS } from '../pages/PrimeCcusPreview';
import { CheckedIcon } from './icons/CheckedIcon';
import { ErrorIcon } from './icons/ErrorIcon';
import { Confirmation, Information } from './modals/common';

import {
  FormList,
  FormListItem,
  FormElement,
} from '../components/CommonFormComponents';

export const CcusDescription = ({ children }) => {
  return <div className='CcusDescription'>{children}</div>;
};

export const ButtonCcus = ({
  lastConnection,
  onClick,
  ccusStatus,
  ccusStateManagementId,
  messageNG,
  editPage,
  isLoadingData,
  isOwner,
}) => {
  const renderStatus = () => {
    if (!ccusStateManagementId && !editPage) {
      return;
    }
    switch (ccusStatus) {
      case CCUS_STATUS.OK:
        const date = ccusStateManagementId ? new Date() : lastConnection;
        return (
          <span className='wrap-status status-success'>
            {`OK（${moment(date).format(DATE_TIME_FORMAT.slashDateFormat)}）`}
          </span>
        );
      case CCUS_STATUS.NG:
        return <span className='wrap-status status-error'>NG</span>;
      case CCUS_STATUS.DEFAULT:
        return <span className='wrap-status status-error'>未確認</span>;
      default:
        return null;
    }
  };

  const renderDescription = () => {
    switch (ccusStatus) {
      case CCUS_STATUS.OK:
        if (ccusStateManagementId) {
          return (
            <span className='wrap-description'>
              <span className='description-success'>
                <CheckedIcon className='CcusForm__check-icon icon--1_6x' />{' '}
                接続に成功しました。
                <br />
              </span>
              このままご利用いただけます。「{editPage ? '更新' : '登録'}
              」ボタンを押してください。
            </span>
          );
        }
        return null;
      case CCUS_STATUS.NG:
        if (ccusStateManagementId) {
          return (
            <span className='wrap-description'>
              <span className='description-error'>
                <ErrorIcon className='CcusForm__error-icon icon--1_6x' />
                {messageUtility.getMessage('E-001-428')}
              </span>
              <span>
                {messageNG}
                {
                  <React.Fragment>
                    <br />
                    <br />
                    {messageUtility.getMessage(
                      isOwner ? 'I-001-521' : 'I-001-522'
                    )}
                  </React.Fragment>
                }
              </span>
            </span>
          );
        }
        return null;
      case CCUS_STATUS.DEFAULT:
        return (
          <span className='wrap-description'>
            <span className='description-default'>
              入力した内容でCCUSに接続し、連携できるか確認します。「CCUSに接続確認」ボタンを押してください。
            </span>
          </span>
        );
      default:
        return (
          <span className='wrap-description'>
            <span className='description-default'>
              入力した内容でCCUSに接続できるか確認します。「CCUSに接続確認」ボタンを押してください。
            </span>
          </span>
        );
    }
  };

  const renderLabelButtons = () => {
    return !_.isNull(ccusStatus) && ccusStatus !== CCUS_STATUS.DEFAULT
      ? 'もう一度接続確認'
      : 'CCUSに接続確認';
  };

  return (
    <div className='wrap-btn wrap-btn-ccus'>
      {!isLoadingData && (
        <React.Fragment>
          <div>
            {renderStatus()}
            <button
              className={`${_.isNull(ccusStatus) ? 'btn--disabled' : 'btn'}`}
              onClick={onClick}
              disabled={_.isNull(ccusStatus)}
            >
              {renderLabelButtons()}
            </button>
          </div>
          {renderDescription()}
        </React.Fragment>
      )}
    </div>
  );
};

export const WarningNotConnectCcus = ({
  isShowWarning,
  onClose,
  onConfirm,
}) => {
  return (
    <Confirmation
      message={
        <React.Fragment>
          CCUSとの連携が確認できていません。 <br />
          CCUSとの連携を確認した上で登録することを推奨しています。連携を行う場合は「戻る」を押してください。
        </React.Fragment>
      }
      title='CCUSと連携できていません'
      cancelButtonText='戻る'
      confirmButtonText='このまま登録'
      visible={isShowWarning}
      onCancel={onClose}
      onConfirm={onConfirm}
    />
  );
};

export const PopupErrorConnectCcus = ({ isShowPopup, message, onClose }) => {
  return (
    <Information
      title='CCUS連携エラー'
      visible={isShowPopup}
      message={message}
      buttonText={messageUtility.getMessage('I-000-017')}
      onClose={onClose}
      multilineText={true}
    />
  );
};

export const PrimeDeleteCCUSConfirmation = props => {
  const { visible, onConfirm, onCancel } = props;
  return (
    <Confirmation
      visible={visible}
      title={messageUtility.getMessage('I-001-451')}
      message={messageUtility.getMessage('I-001-452')}
      confirmButtonText={messageUtility.getMessage('I-000-018')}
      cancelButtonText={messageUtility.getMessage('I-000-008')}
      onConfirm={onConfirm}
      onCancel={onCancel}
      rootClassName='PrimeDeleteCCUSConfirmation'
      isNegative={true}
      multilineText={true}
    />
  );
};

// CCUS事業者IDの削除ダイアログ - BLDE_V2-42611
export const DeleteCCUSConfirmation = props => {
  const {
    dataBasicInfo,
    visible,
    onConfirm,
    onCancel,
    selectedCompanyName,
  } = props;
  const [checked, setChecked] = useState(false);
  const handleAgreement = () => setChecked(!checked);
  const renderMessage = () => {
    return (
      <React.Fragment>
        <div className='DeleteCCUSConfirmation__message'>
          <span className='DeleteCCUSConfirmation__message--attention'>
            CCUS事業者IDの削除を行う会社に誤りがないかご確認ください。
          </span>
          <table className='ccus-information--list'>
            <tbody>
              <tr>
                <td className='ccus-information--list__label'>
                  Buildeeで編集中の会社名<span>：</span>
                </td>
                <td className='ccus-information--list__value'>
                  {selectedCompanyName}
                </td>
              </tr>
              <tr>
                <td className='ccus-information--list__label'>
                  現在Buildeeに登録されている事業者ID<span>：</span>
                </td>
                <td className='ccus-information--list__value'>
                  {dataBasicInfo.ccus_jigyousya_id}
                </td>
              </tr>
            </tbody>
          </table>
          <div className='DeleteCCUSConfirmation__message'>
            CCUS事業者IDを削除するとCCUS連携が解除され以下の影響があります。
            <ul className='DeleteCCUSConfirmation__message--list'>
              <li className='DeleteCCUSConfirmation__message--list-item'>
                この会社に登録されている連携ユーザーがすべて解除されます。
              </li>
              <li className='DeleteCCUSConfirmation__message--list-item'>
                CCUS技能者連携済みの作業員がいる場合、ステータスが「連携解除」になります。
              </li>
              <li className='DeleteCCUSConfirmation__message--list-item'>
                作業員のCCUS連携が解除されることで入退場実績のCCUSへの送信が行われなくなります。
              </li>
              <li className='DeleteCCUSConfirmation__message--list-item'>
                CCUS施工体制連携が行われている場合、連携ステータスが「仮連携」、施工体制技能者連携は「連携解除」になります。
              </li>
            </ul>
          </div>
          <span className='DeleteCCUSConfirmation__message--attention'>
            本当に削除しても問題ない場合は、以下にチェックを入れて「削除」ボタンをクリックしてください。
          </span>
          <div className='DeleteCCUSConfirmation__checkbox'>
            <label className='checkbox'>
              <input
                name='checkbox'
                type='checkbox'
                disabled={false}
                checked={checked}
                onChange={handleAgreement}
              />
              <i className='i-square i-3x' />
              上記の影響が問題ないことを確認した
            </label>
          </div>
        </div>
      </React.Fragment>
    );
  };
  return (
    <Confirmation
      rootClassName={'DeleteCCUSConfirmation'}
      visible={visible}
      title={messageUtility.getMessage('I-000-255')}
      message={renderMessage()}
      confirmButtonText={messageUtility.getMessage('I-000-093')}
      cancelButtonText={messageUtility.getMessage('I-000-008')}
      onConfirm={onConfirm}
      onCancel={() => {
        setChecked(false);
        onCancel();
      }}
      disabledConfirmButton={!checked}
      effectiveButtonClassName='btn--negative'
    />
  );
};

// 代表者名登録への導線ダイアログ - BLDE_V2-42611
export const CCUSNoDelegateNameDialog = props => {
  const { visible, onCancel, onConfirm } = props;
  return (
    <Confirmation
      rootClassName='inline-dialog-md'
      visible={visible}
      title={messageUtility.getMessage('I-000-256')}
      message={messageUtility.getMessage('I-000-257')}
      confirmButtonText={messageUtility.getMessage('I-000-258')}
      cancelButtonText={messageUtility.getMessage('I-000-178')}
      onConfirm={onConfirm}
      onCancel={onCancel}
      disabledConfirmButton={false}
      effectiveButtonClassName={'btn'}
      multilineText
    />
  );
};

export const DeleteCCUSFailureInfo = ({ dialog: { callback } }) => {
  return (
    <Information
      visible={true}
      title={messageUtility.getMessage('E-001-426')}
      message={messageUtility.getMessage('E-001-427')}
      rootClassName='DeleteCCUSFailureInfo'
      buttonText={messageUtility.getMessage('I-000-017')}
      onClose={callback.done}
      multilineText={true}
    />
  );
};

export const ConnectCCUSFailureInfo = ({ dialog: { state, callback } }) => {
  return (
    <Information
      visible={true}
      title={messageUtility.getMessage('E-001-428')}
      message={
        <div>
          {state.messageNG}
          {
            <React.Fragment>
              <br />
              {messageUtility.getMessage(
                state.isOwner ? 'I-001-521' : 'I-001-522'
              )}
            </React.Fragment>
          }
        </div>
      }
      rootClassName={
        state.isOwner
          ? 'ConnectCCUSFailureInfo--isOwner'
          : 'ConnectCCUSFailureInfo--isNotOwner'
      }
      buttonText={messageUtility.getMessage('I-000-017')}
      onClose={callback.done}
      multilineText={true}
    />
  );
};

export const ConnectCCUSMismatchInfo = ({ dialog: { state, callback } }) => {
  const message = (
    <div className='ConnectCCUSMismatchInfo__message'>
      CCUSとの接続確認に失敗しました。
      <br />
      入力されたCCUS事業者ID（{state.ccus_jigyousya_id}）にCCUS管理者ID（
      {state.ccus_company_admin_id}）が所属していません。
      <br />
      現在Buildeeで編集中の
      <span className='ConnectCCUSMismatchInfo__message--attention'>
        {state.responseData.company_name}
      </span>
      の正しいCCUS事業者IDをご確認ください。
      <table className='ccus-information--list'>
        <tr>
          <td className='ccus-information--list__label'>
            入力されたCCUS事業者ID<span>：</span>
          </td>
          <td className='ccus-information--list__value'>
            {state.ccus_jigyousya_id}
          </td>
        </tr>
        <tr>
          <td className='ccus-information--list__label'>
            CCUS管理者IDが所属している事業者ID<span>：</span>
          </td>
          <td className='ccus-information--list__value'>
            {state.responseData.ccus_jigyousya_id}
          </td>
        </tr>
      </table>
      入力されたCCUS事業者IDが正しい場合：CCUS管理者IDの情報を修正してください。
      <br />
      CCUS管理者IDが所属している事業者IDが正しい場合：CCUS事業者IDを修正してください。
    </div>
  );

  return (
    <Information
      visible={true}
      title={messageUtility.getMessage('E-001-428')}
      message={message}
      rootClassName='ConnectCCUSMismatchInfo'
      buttonText={messageUtility.getMessage('I-000-017')}
      onClose={callback.done}
      multilineText={true}
    />
  );
};

export const ConfrictOwnerCCUSInfo = ({ dialog: { state, callback } }) => {
  const message = (
    <div className='ConfrictOwnerCCUSInfo__message'>
      <div className='ConfrictOwnerCCUSInfo__message--section'>
        入力されたCCUS基本情報が自社(
        <span className='ConfrictOwnerCCUSInfo__message--companyName'>
          {state.ownerCompanyName}
        </span>
        )のCCUS基本情報と一致しているため登録に失敗しました。
        <br />
        代行先の会社(
        <span className='ConfrictOwnerCCUSInfo__message--companyName'>
          {state.selectedCompanyName}
        </span>
        )のCCUS基本情報を入力する必要があります。
      </div>
      入力したCCUS基本情報が代行先の情報で間違いがない場合には、自社のCCUS基本情報に代行先のCCUS基本情報を入力している可能性がございます。
    </div>
  );

  return (
    <Information
      visible={true}
      title={messageUtility.getMessage('I-001-513')}
      message={message}
      rootClassName='ConfrictOwnerCCUSInfo'
      buttonText={messageUtility.getMessage('I-000-017')}
      onClose={callback.back}
      multilineText={true}
    />
  );
};

export const DifferentCCUSNameConfirmation = ({
  dialog: { state, callback },
}) => {
  const [checkCompanyData, setCheckCompanyData] = useState({
    name: '',
    validateState: false,
    validateErrorMessage: '',
  });
  const checkCompanySchema = Joi.valid(
    String(state.responseData.ccus_jigyousya_name).normalize()
  );
  const handlecheckCompanyNameChange = e => {
    const result = Joi.validate(
      String(e.target.value).normalize(),
      checkCompanySchema,
      {
        abortEarly: false,
        allowUnknown: true,
      }
    );
    if (result.error === null) {
      setCheckCompanyData({
        name: e.target.value,
        validateState: true,
        validateErrorMessage: '',
      });
    } else {
      setCheckCompanyData({
        name: e.target.value,
        validateState: false,
        validateErrorMessage:
          'CCUSから取得した会社名と完全一致していません。半角全角、大文字小文字、旧字体など含めて完全に一致させてください。',
      });
    }
  };

  const message = (
    <div className='DifferentCCUSNameConfirmation__message'>
      CCUSとの接続確認に成功しましたが、Buildeeで編集中の会社名とCCUSから取得した会社名が異なっています。
      <br />
      <span className='DifferentCCUSNameConfirmation__message--attention'>
        CCUS連携を行う会社に誤りがないかご確認ください。
      </span>
      <table className='ccus-information--list'>
        <tr>
          <td className='ccus-information--list__label'>
            Buildeeで編集中の会社名<span>：</span>
          </td>
          <td className='ccus-information--list__value'>
            {state.responseData.company_name}
          </td>
        </tr>
        <tr>
          <td className='ccus-information--list__label'>
            CCUSから取得した会社名<span>：</span>
          </td>
          <td className='ccus-information--list__value'>
            {state.responseData.ccus_jigyousya_name}
          </td>
        </tr>
      </table>
      <span className='DifferentCCUSNameConfirmation__message--attention'>
        誤りがない場合には、確認のため以下の欄にCCUSから取得した会社名を入力し「
        {messageUtility.getMessage(
          state.isCreateForm ? 'I-000-080' : 'I-000-088'
        )}
        」ボタンを押下してください。
      </span>
      <FormList>
        <FormListItem label='会社名' required={true}>
          <FormElement validate={checkCompanyData.validateErrorMessage}>
            <input
              className='FormInput'
              name='checkCompanyName'
              type='text'
              placeholder='CCUSから取得した会社名'
              value={checkCompanyData.name}
              onChange={handlecheckCompanyNameChange}
            />
          </FormElement>
        </FormListItem>
      </FormList>
    </div>
  );

  return (
    <Confirmation
      visible={true}
      title={messageUtility.getMessage('I-001-514')}
      message={message}
      rootClassName='DifferentCCUSNameConfirmation'
      cancelButtonText={messageUtility.getMessage('I-000-017')}
      confirmButtonText={messageUtility.getMessage(
        state.isCreateForm ? 'I-000-080' : 'I-000-088'
      )}
      disabledConfirmButton={!checkCompanyData.validateState}
      onCancel={callback.back}
      onConfirm={callback.done}
      multilineText={true}
    />
  );
};

export const ConnectCCUSSuccessConfirmation = ({
  dialog: { state, callback },
}) => {
  const message = (
    <div className='ConnectCCUSSuccessConfirmation__message'>
      CCUSとの接続確認に成功しました。
      <br />
      Buildeeで編集中の会社とCCUSから取得した会社の情報は以下の通りです。
      <table className='ccus-information--list'>
        <tr>
          <td className='ccus-information--list__label'>
            Buildeeで編集中の会社名<span>：</span>
          </td>
          <td className='ccus-information--list__value'>
            {state.responseData.company_name}
          </td>
        </tr>
        <tr>
          <td className='ccus-information--list__label'>
            CCUSから取得した会社名<span>：</span>
          </td>
          <td className='ccus-information--list__value'>
            {state.responseData.ccus_jigyousya_name}
          </td>
        </tr>
      </table>
      <span className='ConnectCCUSSuccessConfirmation__message--attention'>
        問題なければ、「
        {messageUtility.getMessage(
          state.isCreateForm ? 'I-000-080' : 'I-000-088'
        )}
        」ボタンを押下してください。
      </span>
    </div>
  );

  return (
    <Confirmation
      visible={true}
      title={messageUtility.getMessage('I-001-453')}
      message={message}
      rootClassName='ConnectCCUSSuccessConfirmation'
      cancelButtonText={messageUtility.getMessage('I-000-017')}
      confirmButtonText={messageUtility.getMessage(
        state.isCreateForm ? 'I-000-080' : 'I-000-088'
      )}
      onCancel={callback.back}
      onConfirm={callback.done}
      multilineText={true}
    />
  );
};



// WEBPACK FOOTER //
// ./src/components/CcusBase.jsx